import { useApiClient } from '@msdk/common'
import { AttachmentFieldDefinition, AttachmentValue, FormFieldDefinition, FormNode } from '@msdk/definitions'
import { ButtonIcon, FileDownloadOutlinedIcon, KeyValue, styled } from '@msdk/ui'
import React, { useMemo } from 'react'

import { useFieldLabel } from '../../hooks'
import { downloadAttachment } from '../../utils'
import { ReadonlyFieldContainer } from './ReadonlyFieldContainer'

const Container = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const StyledButtonIcon = styled(ButtonIcon)`
  max-height: 24px;
`

function isFormFieldDefinition(value: unknown): value is FormFieldDefinition {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const formDefinition = value as Partial<FormFieldDefinition>
  return (
    (formDefinition.hideDescription === null ||
      formDefinition.hideDescription === undefined ||
      typeof formDefinition.hideDescription === 'boolean') &&
    (formDefinition.overrideIDML === null ||
      formDefinition.overrideIDML === undefined ||
      typeof formDefinition.overrideIDML === 'string') &&
    (formDefinition.overrideDescription === null ||
      formDefinition.overrideDescription === undefined ||
      typeof formDefinition.overrideDescription === 'string')
  )
}

function ReadonlyAttachmentContent({
  value,
  fieldDef,
  formDef,
  divider,
  inList,
}: {
  value?: AttachmentValue
  fieldDef: AttachmentFieldDefinition
  formDef: FormFieldDefinition
  divider?: boolean
  inList?: boolean
}) {
  const showLabel = !inList && !formDef.hideDescription
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const api = useApiClient()

  const builtValue = useMemo(() => {
    if (!value || !value.url) {
      return undefined
    }

    return (
      <>
        {fieldDef?.canDownload && (
          <Container>
            {value.toStringForView()}
            <StyledButtonIcon
              color="primary"
              aria-label="Download"
              onClick={() => {
                downloadAttachment(value, api)
              }}
              edge="end"
            >
              <FileDownloadOutlinedIcon />
            </StyledButtonIcon>
          </Container>
        )}
      </>
    )
  }, [value, fieldDef, api])

  return (
    <ReadonlyFieldContainer>
      <KeyValue label={showLabel ? fieldLabel : undefined} direction="column" divider={divider}>
        {builtValue}
      </KeyValue>
    </ReadonlyFieldContainer>
  )
}

export function ReadonlyAttachmentField({
  node,
  divider,
  inList,
}: {
  node: FormNode
  divider?: boolean
  inList?: boolean
}) {
  const { data, fieldDefinition, formDefinition } = node.value
  if (
    !(fieldDefinition instanceof AttachmentFieldDefinition) ||
    (data !== null && data !== undefined && !(data instanceof AttachmentValue)) ||
    !isFormFieldDefinition(formDefinition)
  ) {
    return null
  }

  return (
    <ReadonlyAttachmentContent
      value={data as AttachmentValue}
      fieldDef={fieldDefinition}
      formDef={formDefinition}
      divider={divider}
      inList={inList}
    />
  )
}
